import { useContext, useState } from "react";
import { AccountConfigurationContext } from "@shared/features/configuration/AccountConfiguration/AccountConfigurationProvider";
import { HedgeMode } from "@shared/features/configuration/types";
import {
  ClientData,
  ClientStatus,
  DisplayResultMessage,
  LanguageNameSpaces,
  OpenMessage,
  UsageType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { useChangeAccountStatus } from "@shared/features/configuration/hooks";
import {
  AccountObjectType,
  UseQuotaModal,
} from "@shared/features/settings/types";
import { useClientsData, useExchangesMetadata } from "@shared/utils/hooks/misc";
import { INACTIVE_ACCOUNT_STATUSES } from "@shared/features/portfolio/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  getReadOnlyAccountWarning,
  isHedgeModeSupported,
} from "@shared/utils/misc";

export const useHedgeModeSelector = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { hedgeMode, setHedgeMode, isPreview } = useContext(
    AccountConfigurationContext
  )!;

  const options = Object.values(HedgeMode).map((iterHedgeMode) => ({
    value: iterHedgeMode,
    name: iterHedgeMode,
    isSelected: iterHedgeMode === hedgeMode,
  }));

  const hedgeModeSelectorData = {
    title: t("account_configuration.position_mode"),
    options,
    value: hedgeMode,
    onChange: setHedgeMode,
  };

  return {
    hedgeModeSelectorData,
    isPreview,
  };
};

interface useAccountConfigurationFormProps {
  openMessage: OpenMessage;
  displayResultMessage: DisplayResultMessage;
}
export const useAccountConfigurationForm = ({
  openMessage,
  displayResultMessage,
}: useAccountConfigurationFormProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { accountId, hasPendingChanges, hedgeMode } = useContext(
    AccountConfigurationContext
  )!;
  const [setAccountConfiguration, { isLoading }] =
    sharedLib.sharedApi.configuration.useSetAccountConfigurationMutation();
  const { getAccountById } = useClientsData();
  const account = accountId ? getAccountById(accountId) : undefined;
  const isEmptyConfiguration =
    account && !isHedgeModeSupported(account.exchange);

  const onSaveButtonClick = () => {
    if (isEmptyConfiguration) {
    } else if (!hasPendingChanges) {
      openMessage(t("account_configuration.no_modifications"), "warning");
    } else if (accountId) {
      displayResultMessage(
        setAccountConfiguration({
          account_id: accountId,
          is_hedge_mode: hedgeMode === HedgeMode.HEDGE_MODE,
        }).unwrap(),
        t("account_configuration.update_success")
      ).catch(() => {});
    }
  };

  const saveButtonData = {
    shouldDisplay: !isEmptyConfiguration,
    disabled: !hasPendingChanges,
    isLoading,
    onClick: onSaveButtonClick,
    text: t("account_configuration.save"),
  };

  return {
    saveButtonData,
    isEmptyConfiguration,
  };
};

interface useAccountActionsProps {
  account?: ClientData;
  accountId?: number;
  displayResultMessage: DisplayResultMessage;
  useQuotaModal: UseQuotaModal;
  handleEditClick?: () => void;
  openMessage: OpenMessage;
}

export const useAccountActions = ({
  account: propsAccount,
  accountId,
  displayResultMessage,
  useQuotaModal,
  handleEditClick,
  openMessage,
}: useAccountActionsProps) => {
  const [changeStatusModalStatus, setChangeStatusModalStatus] = useState<
    ClientStatus | undefined
  >(undefined);
  const { quotaModal, handleTriggerClick } = useQuotaModal({
    usageType: UsageType.CLIENT,
  });
  const { getAccountHasActiveDuplicate } = useClientsData();
  const { isLoading: isLoadingChangeStatus, handleChangeClientStatus } =
    useChangeAccountStatus(undefined, displayResultMessage);
  const { getAccountById } = useClientsData();
  const account = propsAccount ?? getAccountById(accountId);
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { getAccountIsReadOnly } = useClientsData();
  const { getIsFree } = useExchangesMetadata();
  const accountIsReadOnly = getAccountIsReadOnly(account?.client_id);
  const accountIsSubUserDeleted =
    account?.client_status === ClientStatus.DELETED_BY_SUB_USER;

  const showNoEditPermissionMessage = () => {
    openMessage?.(
      getReadOnlyAccountWarning(AccountObjectType.ACCOUNT),
      "warning"
    );
  };

  const handleChangeStatusAction = async (clientStatus: ClientStatus) => {
    if (!account) return;
    const isActivate = clientStatus === ClientStatus.ACTIVE;
    const isQuotaError =
      isActivate &&
      !getIsFree(account.exchange) &&
      (await handleTriggerClick({
        ignoreQuotaCheck: getAccountHasActiveDuplicate(account.client_id),
      }));
    if (!isQuotaError) {
      if (isActivate) {
        handleChangeClientStatus([account.client_id], ClientStatus.ACTIVE);
      } else {
        setChangeStatusModalStatus(clientStatus);
      }
    }
  };

  const onEditClick = () => {
    if (accountIsReadOnly) {
      showNoEditPermissionMessage();
      return;
    } else if (accountIsSubUserDeleted) {
      openMessage?.(
        "This account was deleted by it's associated sub-user and cannot be edited.",
        "warning"
      );
      return;
    }
    if (getIsFree(account?.exchange)) {
      handleEditClick?.();
    } else {
      handleTriggerClick({
        ignoreQuotaCheck: true,
      }).then((isQuotaError) => {
        if (!isQuotaError) {
          handleEditClick?.();
        }
      });
    }
  };

  const getChangeStatusButtonData = () => {
    let activationText = t("actions.deactivate");
    let nextStatus = ClientStatus.DEACTIVATED_BY_USER;
    const isInvalidAccount = account?.client_status === ClientStatus.INVALID;
    if (
      INACTIVE_ACCOUNT_STATUSES.has(account?.client_status!) ||
      isInvalidAccount
    ) {
      nextStatus = ClientStatus.ACTIVE;
      activationText = isInvalidAccount
        ? t("actions.re_validate")
        : t("actions.activate");
    }

    return {
      onClick: () => {
        if (accountIsReadOnly) {
          showNoEditPermissionMessage();
        } else {
          handleChangeStatusAction(nextStatus).catch(() => {});
        }
      },
      text: activationText,
      isLoading: isLoadingChangeStatus,
      isPauseIcon: nextStatus !== ClientStatus.ACTIVE,
    };
  };

  const deactivateModalData = {
    accountStatus: changeStatusModalStatus,
    onCancel: () => setChangeStatusModalStatus(undefined),
  };

  const onDeleteClick = () =>
    handleChangeStatusAction(ClientStatus.DELETED).catch(() => {});

  return {
    deactivateModalData,
    changeStatusModalStatus,
    quotaModal,
    onDeleteClick,
    onEditClick,
    changeStatusButtonData: getChangeStatusButtonData(),
    isLoadingChangeStatus,
    account,
    accountIsReadOnly,
    accountIsSubUserDeleted,
  };
};
